import React, { useRef } from 'react';
import {
  StudyListSidebar,
  StudyListSidebarSharedProps,
  sidebarHeadingId,
} from './StudyListSidebar';
import { CriteriaSidebarEmptyState } from './CriteriaSidebarEmptyState';
import { Toast, createToast } from 'components/core';
import {
  EditableCriteria,
  EditableCriteriaHandle,
} from 'components/shared/EditableCriteria';
import { getReviewId } from 'query/review';
import { criteriaWindowName } from 'pages/CriteriaWindow/windowName';

type Props = StudyListSidebarSharedProps & {
  editableCriteriaRef: React.RefObject<EditableCriteriaHandle>;
  disclosureId: string;
  showEmptyState: boolean;
  onCloseConfirmed: () => void;
  reviewId?: string;
};

export const CriteriaSidebar = ({
  editableCriteriaRef,
  disclosureId,
  showEmptyState,
  onCloseConfirmed,
  reviewId,
  ...sidebarProps
}: Props) => {
  const headingId = sidebarHeadingId(disclosureId);
  const criteriaWindowRef = useRef<Window | null>(null);

  const openCriteriaWindow = () => {
    const id = reviewId || getReviewId();
    if (!id) return;

    // Navigating the open popup would discard an edit in progress, so focus it.
    const openWindow = criteriaWindowRef.current;
    if (openWindow && !openWindow.closed) {
      openWindow.focus();
      sidebarProps.onClose();
      return;
    }

    // A per-review window name reuses that review's existing popup instead of
    // spawning duplicates (the fallback when this tab reloaded and lost the
    // ref); opening on click (not on load) keeps this clear of popup blockers.
    const criteriaWindow = window.open(
      `/reviews/${id}/criteria_window`,
      criteriaWindowName(id),
      'popup,width=345,height=600'
    );

    // Popup blocked - keep the criteria panel so the reviewer isn't left with
    // nothing, and say why the window didn't appear.
    if (!criteriaWindow) {
      createToast(
        <Toast type="danger" role="alert">
          Your browser blocked the criteria window. Allow pop-ups for Covidence
          and try again.
        </Toast>
      );
      return;
    }

    criteriaWindowRef.current = criteriaWindow;
    criteriaWindow.focus();
    sidebarProps.onClose();
  };

  return (
    <EditableCriteria
      ref={editableCriteriaRef}
      headingId={headingId}
      pollForUpdates
      onOpenWindow={openCriteriaWindow}
      onCloseConfirmed={onCloseConfirmed}
      emptyState={showEmptyState ? <CriteriaSidebarEmptyState /> : undefined}
    >
      {({ title, panel, isSaving }) => (
        <StudyListSidebar
          {...sidebarProps}
          closeButtonLabel="Close eligibility criteria"
          disclosureId={disclosureId}
          title={title}
          isCloseDisabled={isSaving}
        >
          {panel}
        </StudyListSidebar>
      )}
    </EditableCriteria>
  );
};
